import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import apiClient from "../api/apiClient";
import axios from "axios"; // Import axios directly
import { Helmet } from "react-helmet-async";

const Contacts = () => {
  const [formData, setFormData] = useState({
    nom: "",
    email: "",
    telephone: "",
    message: "",
    quartier: "",
    service: "",
  });

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const navigate = useNavigate();

  const validateForm = () => {
    const errors = {};

    if (!formData.nom.trim()) errors.nom = "Le nom est obligatoire";

    if (!formData.email.trim()) {
      errors.email = "L'email est obligatoire";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Format d'email invalide";
    }

    if (!formData.telephone.trim()) {
      errors.telephone = "Le téléphone est obligatoire";
    } else if (!/^[+]?[0-9\s\-\(\)]{8,}$/.test(formData.telephone)) {
      errors.telephone = "Format de téléphone invalide";
    }

    if (!formData.message.trim()) errors.message = "Le message est obligatoire";
    if (!formData.service) errors.service = "Veuillez sélectionner un service";

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: "" }));
    }

    // Clear general error when user interacts with form
    if (errorMessage) {
      setErrorMessage("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setErrorMessage("Veuillez corriger les erreurs dans le formulaire.");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    // Declare timeoutId at the function scope so it's accessible in catch block
    let timeoutId;

    try {
      // Add timeout to the request using axios directly
      const source = axios.CancelToken.source();
      timeoutId = setTimeout(() => source.cancel("Request timeout"), 15000);

      const response = await apiClient.post(`/contacts`, formData, {
        cancelToken: source.token,
        timeout: 15000,
      });

      clearTimeout(timeoutId);

      setSuccessMessage(
        response.data.message ||
          "Votre demande a été envoyée avec succès ! Nous vous recontacterons rapidement."
      );

      // Reset form
      setFormData({
        nom: "",
        email: "",
        telephone: "",
        message: "",
        quartier: "",
        service: "",
      });
      setFieldErrors({});

      // Redirect after longer delay to let user read success message
      setTimeout(() => {
        setSuccessMessage("");
        navigate("/");
      }, 5000);
    } catch (error) {
      // Clear timeout in catch block - now timeoutId is accessible
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      let errorMsg = "Erreur lors de l'envoi de votre demande.";

      // Use axios.isCancel directly
      if (axios.isCancel(error)) {
        errorMsg = "Délai d'attente dépassé. Veuillez réessayer.";
      } else if (error.response) {
        // Server responded with error status
        if (error.response.status === 422) {
          errorMsg =
            "Données invalides. Veuillez vérifier les informations saisies.";
        } else if (error.response.status >= 500) {
          errorMsg =
            "Erreur serveur. Veuillez réessayer dans quelques instants.";
        } else {
          errorMsg =
            error.response.data?.message || `Erreur ${error.response.status}`;
        }
      } else if (error.request) {
        // No response received
        errorMsg =
          "Problème de connexion. Vérifiez votre connexion internet et réessayez.";
      }

      setErrorMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // JSON-LD structured data for ContactPage
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "Page de Contact - DVELGE GROUP",
    url: "https://www.dvelgegroup.com/contacts",
    contactOption: ["TollFree", "Customer Support"],
    email: "info@dvelgegroup.com",
    telephone: "+224623300992",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Conakry",
      addressCountry: "GN",
    },
  };

  return (
    <div>
      <Helmet>
        <title>Contactez DVELGE GROUP | Parlons de vos projets</title>
        <meta
          name="description"
          content="Contactez DVELGE GROUP pour vos projets digitaux et informatiques. Remplissez notre formulaire ou appelez-nous pour plus d'informations."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Contactez DVELGE GROUP | Parlons de vos projets"
        />
        <meta
          property="og:description"
          content="Remplissez notre formulaire de contact pour discuter de vos besoins digitaux et informatiques."
        />
        <meta property="og:url" content="https://www.dvelgegroup.com/contact" />
        <meta
          property="og:image"
          content="https://www.dvelgegroup.com/images/logo.png"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Contactez DVELGE GROUP | Parlons de vos projets"
        />
        <meta
          name="twitter:description"
          content="Remplissez notre formulaire de contact pour discuter de vos besoins digitaux et informatiques."
        />
        <meta
          name="twitter:image"
          content="https://www.dvelgegroup.com/images/logo.png"
        />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Header Contact */}
      <div className="contact-head-sec r-bg-a pt85 pb120">
        <div className="container">
          <div className="row pt80">
            <div className="col-lg-5 vcenter">
              <div className="page-headings">
                <span className="sub-heading mb15">
                  <i className="fas fa-headset mr5"></i> Parlons-en
                </span>
                <h1 className="mb15">
                  Les meilleurs projets naissent d'échanges sincères
                </h1>
                <p>Rejoignez ceux qui ont fait le choix de la fiabilité</p>
              </div>
            </div>
            <div className="col-lg-7 vcenter">
              <div className="row">
                <div className="col-lg-6 m-mt30">
                  <div className="contact-details-block">
                    <div className="ree-row-set">
                      <div className="ree-icon-set dtb-icon">
                        <i className="fas fa-phone-alt"></i>
                      </div>
                      <div className="ree-details-set dtb-details">
                        <span>Info</span>
                        <Link to="tel:+224623300992">+224 623 30 09 92</Link>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-6 m-mt30">
                  <div className="contact-details-block">
                    <div className="ree-row-set">
                      <div className="ree-icon-set dtb-icon">
                        <i className="fas fa-phone-alt"></i>
                      </div>
                      <div className="ree-details-set dtb-details">
                        <span>Ventes</span>
                        <Link to="tel:+224627020871">+224 627 02 08 71</Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row mt30">
                <div className="col-lg-6">
                  <div className="contact-details-block">
                    <div className="ree-row-set">
                      <div className="ree-icon-set dtb-icon">
                        <i className="fas fa-envelope"></i>
                      </div>
                      <div className="ree-details-set dtb-details">
                        <span>Info</span>
                        <Link to="mailto:info@dvelgegroup.com">
                          info@dvelgegroup.com
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-6 m-mt30">
                  <div className="contact-details-block">
                    <div className="ree-row-set">
                      <div className="ree-icon-set dtb-icon">
                        <i className="fas fa-envelope"></i>
                      </div>
                      <div className="ree-details-set dtb-details">
                        <span>Ventes</span>
                        <Link to="mailto:ventes@dvelgegroup.com">
                          ventes@dvelgegroup.com
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Formulaire de contact */}
      <div className="contact-form-sec sec-pad r-bg-d" id="demand">
        <div className="container">
          <div className="row flx-end">
            <div className="col-lg-6">
              <div className="sec-heading m-center">
                <span className="sub-heading mb15">DEMANDE</span>
                <h2 className="mb15">
                  <span className="ree-text rt40">Fait confiance</span> par
                  beaucoup
                </h2>
                <p>
                  Nous serions heureux de vous entendre, veuillez remplir le
                  formulaire ci-dessous ou nous envoyer vos besoins par mail
                </p>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="form-contact-hom m-mt60">
                <div className="form-block bg-w">
                  <div className="form-head">
                    <h4>
                      Veuillez remplir le formulaire ci-dessous ou envoyez-nous
                      vos exigences :)
                    </h4>
                  </div>
                  <div className="form-body">
                    {successMessage && (
                      <div className="alert alert-success d-flex align-items-center">
                        <i className="fas fa-check-circle me-2"></i>
                        {successMessage}
                        <button
                          type="button"
                          className="btn-close ms-auto"
                          onClick={() => setSuccessMessage("")}
                          aria-label="Fermer"
                        ></button>
                      </div>
                    )}

                    {errorMessage && (
                      <div className="alert alert-danger d-flex align-items-center">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        {errorMessage}
                        <button
                          type="button"
                          className="btn-close ms-auto"
                          onClick={() => setErrorMessage("")}
                          aria-label="Fermer"
                        ></button>
                      </div>
                    )}

                    <form onSubmit={handleSubmit} noValidate>
                      <div className="fieldsets row">
                        <div className="col-md-6">
                          <input
                            type="text"
                            placeholder="Nom complet"
                            name="nom"
                            value={formData.nom}
                            onChange={handleChange}
                            className={fieldErrors.nom ? "is-invalid" : ""}
                          />
                          {fieldErrors.nom && (
                            <div className="invalid-feedback d-block">
                              {fieldErrors.nom}
                            </div>
                          )}
                        </div>
                        <div className="col-md-6">
                          <input
                            type="email"
                            placeholder="Adresse e-mail"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className={fieldErrors.email ? "is-invalid" : ""}
                          />
                          {fieldErrors.email && (
                            <div className="invalid-feedback d-block">
                              {fieldErrors.email}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="fieldsets row">
                        <div className="col-md-6">
                          <input
                            type="tel"
                            placeholder="Numéro de contact"
                            name="telephone"
                            value={formData.telephone}
                            onChange={handleChange}
                            className={
                              fieldErrors.telephone ? "is-invalid" : ""
                            }
                          />
                          {fieldErrors.telephone && (
                            <div className="invalid-feedback d-block">
                              {fieldErrors.telephone}
                            </div>
                          )}
                        </div>
                        <div className="col-md-6">
                          <input
                            type="text"
                            placeholder="Votre Quartier"
                            name="quartier"
                            value={formData.quartier}
                            onChange={handleChange}
                          />
                        </div>
                      </div>

                      <div className="fieldsets row">
                        <div className="col-md-12">
                          <textarea
                            placeholder="Message"
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            className={fieldErrors.message ? "is-invalid" : ""}
                            rows="5"
                          />
                          {fieldErrors.message && (
                            <div className="invalid-feedback d-block">
                              {fieldErrors.message}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="fieldsets">
                        <select
                          name="service"
                          value={formData.service}
                          onChange={handleChange}
                          className={fieldErrors.service ? "is-invalid" : ""}
                        >
                          <option value="">Sélectionnez un service</option>
                          <option value="web_design">
                            Conception de site web
                          </option>
                          <option value="graphic_design">
                            Conception graphique
                          </option>
                          <option value="app_dev">
                            Développement d'applications
                          </option>
                          <option value="digital_marketing">
                            Marketing digital
                          </option>
                        </select>
                        {fieldErrors.service && (
                          <div className="invalid-feedback d-block">
                            {fieldErrors.service}
                          </div>
                        )}
                      </div>

                      <div className="fieldsets mt20">
                        <button
                          type="submit"
                          className="ree-btn ree-btn-grdt1 w-100"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              ></span>
                              Envoi en cours...
                            </>
                          ) : (
                            <>
                              Envoyer votre demande
                              <i className="fas fa-arrow-right fa-btn"></i>
                            </>
                          )}
                        </button>
                      </div>

                      <div className="fieldsets mt-3">
                        <p className="text-center text-muted small">
                          Nous vous recontacterons dans les plus brefs délais
                        </p>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contacts;
